// src/components/auth/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import axiosConfig from '../../api/axiosConfig';
import { STORAGE_KEYS } from '../../utils/constants';
import { useTranslation } from 'react-i18next';

const Logo = ({ size = 40 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <path d="M50 5 L90 25 L90 65 Q90 85 50 97 Q10 85 10 65 L10 25 Z" fill="#1d4ed8" />
        <path d="M50 12 L84 30 L84 64 Q84 80 50 91 Q16 80 16 64 L16 30 Z" fill="#2563eb" />
        <text x="28" y="58" fontFamily="Arial" fontWeight="bold" fontSize="32" fill="white" fontStyle="italic">i</text>
        <text x="42" y="62" fontFamily="Arial" fontWeight="bold" fontSize="36" fill="white">P</text>
    </svg>
);

const PASSWORD_RULES = [
    { id: 'length', label: 'Minimo 8 caratteri', test: p => p.length >= 8 },
    { id: 'upper', label: 'Una lettera maiuscola', test: p => /[A-Z]/.test(p) },
    { id: 'lower', label: 'Una lettera minuscola', test: p => /[a-z]/.test(p) },
    { id: 'number', label: 'Un numero', test: p => /[0-9]/.test(p) },
];

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [tokenValid, setTokenValid] = useState(null); // null=loading, true, false
    const [userEmail, setUserEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Verifica token al mount
    useEffect(() => {
        const verify = async () => {
            try {
                const res = await axiosConfig.get(`/auth/reset-password/${token}`);
                setUserEmail(res.data.data.email);
                setTokenValid(true);
            } catch {
                setTokenValid(false);
            }
        };
        if (token) verify();
    }, [token]);

    const isPasswordValid = PASSWORD_RULES.every(r => r.test(password));
    const score = PASSWORD_RULES.filter(r => r.test(password)).length;
    const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-emerald-500'];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isPasswordValid) { setError('La password non soddisfa i requisiti'); return; }
        if (password !== confirm) { setError('Le password non coincidono'); return; }

        setLoading(true);
        setError('');
        try {
            const res = await axiosConfig.post(`/auth/reset-password/${token}`, { password });
            // Auto-login con token ricevuto
            if (res.data.data?.token) {
                localStorage.setItem(STORAGE_KEYS.TOKEN, res.data.data.token);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, res.data.data.refreshToken || '');
            }
            setSuccess(true);
            setTimeout(() => navigate('/dashboard'), 2000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Errore durante il reset. Riprova.');
        } finally {
            setLoading(false);
        }
    };

    const ic = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition';

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative max-w-md w-full mx-4">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Logo size={48} />
                        <div className="text-left">
                            <h1 className="text-2xl font-bold text-white">InfraPilot</h1>
                            <p className="text-blue-300 text-xs">Construction Site Management</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8">

                    {/* Loading verifica token */}
                    {tokenValid === null && (
                        <div className="text-center py-8">
                            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">Verifica link in corso...</p>
                        </div>
                    )}

                    {/* Token non valido */}
                    {tokenValid === false && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl">⚠️</span>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Link non valido</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                Il link di reset è scaduto o non è valido. I link scadono dopo 1 ora.
                            </p>
                            <Link to="/forgot-password"
                                className="block w-full text-center py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition">
                                Richiedi nuovo link
                            </Link>
                        </div>
                    )}

                    {/* Successo */}
                    {success && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl">✅</span>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Password aggiornata!</h2>
                            <p className="text-gray-500 text-sm">Reindirizzamento alla dashboard...</p>
                        </div>
                    )}

                    {/* Form reset */}
                    {tokenValid === true && !success && (
                        <>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Nuova password</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                Scegli una nuova password per <strong>{userEmail}</strong>
                            </p>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                                    ⚠️ {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Password */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nuova password</label>
                                    <div className="relative">
                                        <input type={showPass ? 'text' : 'password'}
                                            value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                                            className={`${ic} pr-10`} placeholder="••••••••" autoComplete="new-password" />
                                        <button type="button" onClick={() => setShowPass(p => !p)} tabIndex={-1}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                            {showPass ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    {password && (
                                        <>
                                            <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className={`h-full transition-all ${strengthColors[score - 1] || 'bg-gray-200'}`}
                                                    style={{ width: `${(score / 4) * 100}%` }} />
                                            </div>
                                            <div className="mt-2 grid grid-cols-2 gap-1">
                                                {PASSWORD_RULES.map(rule => {
                                                    const ok = rule.test(password);
                                                    return (
                                                        <div key={rule.id} className="flex items-center gap-1">
                                                            {ok
                                                                ? <CheckCircleIcon className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                                                : <XCircleIcon className="w-3 h-3 text-gray-300 flex-shrink-0" />
                                                            }
                                                            <span className={`text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>{rule.label}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Conferma */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Conferma password</label>
                                    <div className="relative">
                                        <input type={showConfirm ? 'text' : 'password'}
                                            value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }}
                                            className={`${ic} pr-10 ${confirm && password === confirm ? '!border-emerald-400 !bg-emerald-50' : ''}`}
                                            placeholder="••••••••" autoComplete="new-password" />
                                        <button type="button" onClick={() => setShowConfirm(p => !p)} tabIndex={-1}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                            {showConfirm ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    {confirm && password === confirm && (
                                        <p className="text-emerald-600 text-xs mt-1 flex items-center gap-1">
                                            <CheckCircleIcon className="w-3 h-3" /> Le password coincidono
                                        </p>
                                    )}
                                </div>

                                <button type="submit"
                                    disabled={loading || !isPasswordValid || password !== confirm}
                                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50 text-sm">
                                    {loading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Aggiornamento...
                                        </span>
                                    ) : 'Aggiorna password'}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                <p className="text-center text-xs text-slate-500 mt-6">
                    © {new Date().getFullYear()} InfraPilot · Tutti i diritti riservati
                </p>
            </div>
        </div>
    );
};

export default ResetPassword;